import { useAuth0 } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import API_Calls_DataFile from "@/api/calls/API_Calls_DataFile";
import AreYouSureModal from "@/components/membership/Modals/AreYouSureModal";
import CircularLoaderWithPercentage from "@/components/membership/Template/CircularLoaderWithPercentage";
import Notifications from "@/components/Notifications/Notifications";
import SearchBar from "@/components/SearchBar/SearchBar";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import TableStyles from "@/styles/TableStyles";

const UploadsTable = ({
  files,
  handleFileSelection,
  handleFileDelete,
  isLoading,
  filesAwaitingColumns,
}) => {
  const [data, setData] = useState(files);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth0();
  const [notification, setNotification] = useState({});
  const { downloadFile } = API_Calls_DataFile();

  useEffect(() => {
    setData(files);
  }, [files]);

  const filteredData =
    searchTerm.length === 0
      ? data
      : data.filter((item) =>
          item.filename.toLowerCase().includes(searchTerm.toLowerCase())
        );

  const handleFileDownload = async (item) => {
    setNotification({
      message: "Download started",
      severity: "info",
      duration: 3000,
    });
    const response = await downloadFile(
      user.org_name,
      item.filename,
      item.created
    );
    if (response.error) {
      setNotification({
        message: "Unable to download at this time",
        severity: "error",
        duration: 7000,
      });
    }
  };

  return (
    <>
      <div className="template-tables-searchbar-cont">
        <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      </div>
      <TableContainer
        id="uploads-table"
        data-testid="uploads-table"
        sx={TableStyles.Container}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={TableStyles.THFont} align="left">
                  File Name
                </TableCell>
                <TableCell sx={TableStyles.THFont} align="left">
                  Uploaded
                </TableCell>
                <TableCell sx={TableStyles.THFont} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      {filesAwaitingColumns.includes(
                        `${row.filename}${row.created}`
                      ) ? (
                        <CircularLoaderWithPercentage
                          totalSeconds={parseInt(row.size / 1000000 + 1)}
                        />
                      ) : (
                        <Button
                          className="siteBlue"
                          onClick={() => handleFileSelection(row)}
                        >
                          Select
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="left">{row.filename}</TableCell>
                    <TableCell align="left">{row.created}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        sx={TableStyles.TableButton}
                        onClick={() => handleFileDownload(row)}
                      >
                        <SiteIcons iconstring="download" />
                      </IconButton>
                      <IconButton>
                        <AreYouSureModal
                          id={row}
                          handleAction={handleFileDelete}
                          title={`Delete ${row.filename}`}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    No uploaded files to display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
};

UploadsTable.propTypes = {
  files: PropTypes.array,
  handleFileSelection: PropTypes.func,
  handleFileDelete: PropTypes.func,
  isLoading: PropTypes.bool,
  filesAwaitingColumns: PropTypes.array,
};

export default UploadsTable;
